import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Book, Calendar, Clock, Eye, Share2, BookOpen, Lock, User, Mail, ExternalLink, ChevronDown, ChevronUp, ChevronRight, Facebook, Twitter, Linkedin, Copy, X, ArrowLeft as ArrowLeftIcon, ArrowRight, DollarSign, Percent, FileText, MessageCircle, BarChart3 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { BookItem } from '../components/Elze/BookItem';
import { books, getBookById } from '../data/books';
import { currentUser } from '../data/currentUser';
export function PublicBookPage() {
  const {
    bookId
  } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [accessError, setAccessError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showReaderModal, setShowReaderModal] = useState(false);
  const [hasAccessToPreview, setHasAccessToPreview] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(5); // Esempio, in un'app reale questo verrebbe calcolato
  // Find the book with the matching ID from centralized data
  const book = getBookById(bookId || '');
  // If book not found, show error
  if (!book) {
    return <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-1174">
        <div className="text-center" data-id="element-1175">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1176">
            Opera non trovata
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-1177">
            L'opera che stai cercando non esiste o è stata rimossa.
          </p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-[#161c2d] text-white rounded-md hover:bg-[#1e2740] transition-colors" data-id="element-1178">
            Torna alla Home
          </button>
        </div>
      </div>;
  }
  // Effect to set preview content when access is granted
  useEffect(() => {
    if (hasAccessToPreview && book.preview && book.preview.content) {
      setPreviewContent(book.preview.content);
    }
  }, [hasAccessToPreview, book]);
  // Check if preview is available
  const isPreviewAvailable = () => {
    if (!book.preview || !book.preview.enabled) return false;
    // Check time window if enabled
    if (book.preview.timeWindow && book.preview.timeWindow.enabled) {
      const now = new Date();
      const startDate = new Date(book.preview.timeWindow.startDate);
      const endDate = new Date(book.preview.timeWindow.endDate);
      if (now < startDate || now > endDate) return false;
    }
    return true;
  };
  // Check whitelist access
  const checkWhitelistAccess = () => {
    if (!email) {
      setAccessError('Inserisci un indirizzo email');
      return;
    }
    // Se non c'è whitelist o l'email è nella whitelist
    if (book.preview.whitelist.length === 0 || book.preview.whitelist.includes(email.trim().toLowerCase())) {
      setHasAccessToPreview(true);
      setShowReaderModal(true);
      setAccessError('');
      setShowAccessForm(false);
    } else {
      setAccessError('Non hai accesso a questa preview');
    }
  };
  // Open reader modal with access check
  const openReaderModal = () => {
    // Se la preview non è disponibile, non fare nulla
    if (!isPreviewAvailable()) return;
    // Se non c'è whitelist o l'utente ha già accesso, apri direttamente
    if (book.preview.whitelist.length === 0 || hasAccessToPreview) {
      setHasAccessToPreview(true);
      setShowReaderModal(true);
    } else {
      // Mostra il form di accesso
      setShowAccessForm(true);
    }
  };
  // Format date for display
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  // Share book
  const shareBook = () => {
    setShowShareModal(true);
  };
  // Copy link to clipboard
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    // In a real app, you would show a toast notification here
    alert('Link copiato negli appunti!');
  };
  // Navigate to internal book page - standardized navigation
  const goToInternalPage = () => {
    navigate(`/book/${bookId}`);
  };
  // Navigation for reader
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  // Handler for book clicks - standardized navigation
  const handleBookClick = (bookId: string) => {
    navigate(`/book/public/${bookId}`);
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-1179">
      {/* Header con sfondo trasparente, logo a sinistra e CTA a destra */}
      <div className="absolute top-0 left-0 right-0 z-20" data-id="element-1180">
        <div className="max-w-7xl mx-auto px-8 sm:px-10 lg:px-12" data-id="element-1181">
          <div className="flex justify-between items-center h-16" data-id="element-1182">
            <div className="flex items-center" data-id="element-1183">
              <Link to="/dashboard" className="flex items-center" data-id="element-1184">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jJkprGARCzfGvbLTaJLb3b/Elze_logo.svg" alt="Elze Logo" className="h-8 w-auto filter brightness-0 invert-[1]" data-id="element-1185" />
              </Link>
            </div>
            <div className="flex items-center space-x-4" data-id="element-1186">
              <div className="hidden sm:block text-white text-sm" data-id="element-1187">
                Sei un autore?
              </div>
              <Link to="/dashboard" className="py-2 px-4 bg-[#C6D6AB] hover:bg-[#b8c99a] text-gray-800 text-sm font-medium rounded-md transition-colors backdrop-blur-sm" data-id="element-1188">
                Entra in Elze
              </Link>
              {/* Pulsante condivisione profilo */}
              <button onClick={() => setShowShareModal(true)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur-sm" title="Condividi profilo" data-id="element-1189">
                <Share2 size={16} data-id="element-1190" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Blurred Background */}
      <div className="w-full h-[320px] relative overflow-hidden" style={{
      backgroundImage: `url(${book.cover})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} data-id="element-1191">
        {/* Blur overlay */}
        <div className="absolute inset-0 backdrop-blur-xl bg-[#376770]/30" data-id="element-1192"></div>
      </div>

      {/* Book Details Section - updated to match header width */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10" data-id="element-1193">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8" data-id="element-1194">
          {/* Book Cover */}
          <div className="flex-shrink-0 w-[200px] md:w-[240px] mx-auto md:mx-0" data-id="element-1195">
            <div className="aspect-[1/1.5] shadow-xl rounded-lg overflow-hidden" data-id="element-1196">
              <img src={book.cover} alt={book.title} className="w-full h-full object-cover" data-id="element-1197" />
            </div>
            {/* Internal book link below cover */}
            <div className="mt-4 text-center" data-id="element-1198">
              <button onClick={goToInternalPage} className="inline-flex items-center text-sm font-medium text-[#161c2d] hover:text-[#1e2740]" data-id="element-1199">
                <Book size={14} className="mr-1.5" data-id="element-1200" />
                Vai alla pagina interna
                <ExternalLink size={12} className="ml-1.5" data-id="element-1201" />
              </button>
            </div>
          </div>

          {/* Book Info */}
          <div className="flex-1 bg-white rounded-lg shadow-md p-6 md:p-8" data-id="element-1202">
            <div className="mb-1 text-sm text-[#161c2d]/80 font-medium" data-id="element-1203">
              {book.genre}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-1204">
              {book.title}
            </h1>

            {/* Author info with link to profile - Now showing logged-in user */}
            <div className="flex items-center mb-6" data-id="element-1205">
              <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-1206">
                <img src={currentUser.image} alt={currentUser.name} className="w-full h-full object-cover" data-id="element-1207" />
              </div>
              <div data-id="element-1208">
                <Link to={`/profile/${currentUser.id}`} className="text-sm font-medium text-gray-900 hover:text-[#161c2d] flex items-center" data-id="element-1209">
                  {currentUser.name}
                  <ExternalLink size={14} className="ml-1.5 opacity-70" data-id="element-1210" />
                </Link>
                <div className="text-xs text-gray-500" data-id="element-1211">Autore</div>
              </div>
            </div>

            {/* Publishing Details */}
            <div className="mb-6" data-id="element-1212">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1213">
                <div data-id="element-1214">
                  <h3 className="text-xs font-medium text-gray-500 mb-1" data-id="element-1215">
                    Editore
                  </h3>
                  <p className="text-sm text-gray-900" data-id="element-1216">{book.publisher}</p>
                </div>
                <div data-id="element-1217">
                  <h3 className="text-xs font-medium text-gray-500 mb-1" data-id="element-1218">
                    Anno
                  </h3>
                  <p className="text-sm text-gray-900" data-id="element-1219">{book.year}</p>
                </div>
                <div data-id="element-1220">
                  <h3 className="text-xs font-medium text-gray-500 mb-1" data-id="element-1221">
                    Pagine
                  </h3>
                  <p className="text-sm text-gray-900" data-id="element-1222">{book.pages}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6" data-id="element-1223">
              <h2 className="text-base font-semibold text-gray-900 mb-2" data-id="element-1224">
                Descrizione
              </h2>
              <p className="text-sm text-gray-700" data-id="element-1225">{book.description}</p>
            </div>

            {/* Preview Button - Always visible */}
            <div className="border rounded-lg p-4 bg-gray-50" data-id="element-1226">
              <div className="flex items-center mb-3" data-id="element-1227">
                <Eye size={18} className="text-[#161c2d] mr-2" data-id="element-1228" />
                <h3 className="text-base font-medium text-gray-900" data-id="element-1229">
                  Anteprima{' '}
                  {isPreviewAvailable() ? 'Disponibile' : 'Non Disponibile'}
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3" data-id="element-1230">
                {isPreviewAvailable() ? `Leggi gratuitamente il ${book.preview.percentage}% di questo libro.` : "L'anteprima di questo libro non è attualmente disponibile."}
              </p>
              <button onClick={openReaderModal} className={`w-full px-4 py-2 rounded-md text-sm transition-colors ${isPreviewAvailable() ? 'bg-[#C6D6AB] text-gray-800 hover:bg-[#b8c99a]' : 'bg-gray-300 text-gray-700 cursor-not-allowed'}`} disabled={!isPreviewAvailable()} data-id="element-1231">
                {isPreviewAvailable() ? 'Leggi Anteprima' : 'Anteprima Non Disponibile'}
              </button>
              {isPreviewAvailable() && book.preview.timeWindow && book.preview.timeWindow.enabled && <div className="flex items-center mt-4 text-xs text-gray-500" data-id="element-1232">
                    <Clock size={14} className="mr-1" data-id="element-1233" />
                    <span data-id="element-1234">
                      Disponibile fino al{' '}
                      {formatDate(book.preview.timeWindow.endDate)}
                    </span>
                  </div>}
            </div>
          </div>
        </div>

        {/* Rights Transfer Section - ALWAYS VISIBLE NOW */}
        <div className="mt-8" data-id="element-1235">
          <div className="bg-white rounded-lg border border-gray-200/70 overflow-hidden" data-id="element-1236">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100" data-id="element-1237">
              <div className="flex items-center" data-id="element-1238">
                <h2 className="text-base font-semibold text-gray-900" data-id="element-1239">
                  Investimento in Royalties
                </h2>
              </div>
            </div>
            <div className="px-4 sm:px-6 py-6" data-id="element-1240">
              {book.rightsTransfer && book.rightsTransfer.active ? <>
                  <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1241">
                    <div className="border border-gray-200 rounded-md p-4" data-id="element-1242">
                      <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-1243">
                        Percentuale Diritti in Cessione
                      </div>
                      <div className="text-xl font-bold text-gray-900" data-id="element-1244">
                        {book.rightsTransfer.rightsPercentage}%
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-md p-4" data-id="element-1245">
                      <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-1246">
                        Prezzo Totale
                      </div>
                      <div className="text-xl font-bold text-gray-900" data-id="element-1247">
                        {book.rightsTransfer.budget}
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-md p-4" data-id="element-1248">
                      <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-1249">
                        Investimento Minimo
                      </div>
                      <div className="text-xl font-bold text-gray-900" data-id="element-1250">
                        0,5%
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-1251">
                        Equivalente a{' '}
                        {(parseFloat(book.rightsTransfer.budget ? book.rightsTransfer.budget.replace('€', '').replace(',', '') : '0') * 0.005).toLocaleString('it-IT', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 0
                    })}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-3" data-id="element-1252">
                    <button className="flex-1 px-4 py-3 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors text-center" data-id="element-1253">
                      Contatta Elze per Acquisto
                    </button>
                    <button className="flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-center" data-id="element-1254">
                      Visualizza Contratto di Cessione
                    </button>
                  </div>
                </> : <div className="text-center py-8" data-id="element-1255">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-1256">
                    <Percent size={24} className="text-gray-400" data-id="element-1257" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1258">
                    Nessuna campagna di investimento attiva
                  </h3>
                  <p className="text-sm text-gray-500 max-w-md mx-auto mb-6" data-id="element-1259">
                    Al momento non ci sono opportunità di investimento in
                    royalties per questa opera. Le campagne di investimento
                    permettono di acquisire una percentuale dei diritti
                    d'autore.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1260">
                    <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm" data-id="element-1261">
                      Contatta l'Autore
                    </button>
                    <button className="px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors text-sm" data-id="element-1262">
                      Ricevi Notifiche su Nuove Campagne
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Author Bio with link to profile - Now showing logged-in user */}
        <div className="mt-8" data-id="element-1263">
          <div className="bg-white rounded-lg border border-gray-200/70 overflow-hidden" data-id="element-1264">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100" data-id="element-1265">
              <div className="flex items-center" data-id="element-1266">
                <h2 className="text-base font-semibold text-gray-900" data-id="element-1267">
                  L'Autore
                </h2>
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4" data-id="element-1268">
              <div className="flex flex-col sm:flex-row items-start gap-4 p-4" data-id="element-1269">
                <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 mx-auto sm:mx-0" data-id="element-1270">
                  <img src={currentUser.image} alt={currentUser.name} className="w-full h-full object-cover" data-id="element-1271" />
                </div>
                <div data-id="element-1272">
                  <Link to={`/profile/${currentUser.id}`} className="text-lg font-semibold text-gray-900 mb-2 text-center sm:text-left hover:text-[#161c2d] flex items-center" data-id="element-1273">
                    {currentUser.name}
                    <ExternalLink size={16} className="ml-2 opacity-70" data-id="element-1274" />
                  </Link>
                  <p className="text-sm text-gray-700" data-id="element-1275">{currentUser.bio}</p>
                  <div className="mt-4 flex justify-center sm:justify-start space-x-3" data-id="element-1276">
                    <button className="flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors" data-id="element-1277">
                      <Mail size={14} className="mr-1.5" data-id="element-1278" />
                      Contatta
                    </button>
                    <Link to={`/profile/${currentUser.id}`} className="flex items-center px-3 py-1.5 bg-[#C6D6AB] text-gray-800 rounded-md text-sm hover:bg-[#b8c99a] transition-colors" data-id="element-1279">
                      Visualizza Profilo
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Altre opere dell'autore - Now showing logged-in user's books */}
        <div className="mt-8" data-id="element-1280">
          <div className="bg-white rounded-lg border border-gray-200/70 overflow-hidden" data-id="element-1281">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100" data-id="element-1282">
              <div className="flex items-center" data-id="element-1283">
                <h2 className="text-base font-semibold text-gray-900" data-id="element-1284">
                  Altre opere dell'autore
                </h2>
              </div>
              <div data-id="element-1285">
                <Link to={`/profile/${currentUser.id}`} className="text-sm text-[#161c2d] flex items-center hover:underline" data-id="element-1286">
                  Vedi tutte <ChevronRight size={16} data-id="element-1287" />
                </Link>
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4" data-id="element-1288">
              <div className="overflow-x-auto scrollbar-hide" data-id="element-1289">
                <div className="flex space-x-4 sm:space-x-5 w-max pb-2" data-id="element-1290">
                  {books.filter(b => b.author.id === currentUser.id && b.id !== book.id).slice(0, 5).map(authorBook => <div key={authorBook.id} className="w-[180px] flex-shrink-0" data-id="element-1291">
                        <BookItem cover={authorBook.cover} title={authorBook.title} genre={authorBook.genre} year={authorBook.year} authorName={currentUser.name} authorImage={currentUser.image} id={authorBook.id} onClick={() => handleBookClick(authorBook.id)} data-id="element-1292" />
                      </div>)}
                  {books.filter(b => b.author.id === currentUser.id && b.id !== book.id).length === 0 && <div className="py-8 text-center w-full" data-id="element-1293">
                      <p className="text-gray-500" data-id="element-1294">
                        Nessun'altra opera disponibile
                      </p>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Access Form Modal */}
        {showAccessForm && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1295">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-1296">
              <div className="flex items-center justify-between mb-4" data-id="element-1297">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-1298">
                  Accesso all'Anteprima
                </h3>
                <button onClick={() => setShowAccessForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1299">
                  <X size={20} data-id="element-1300" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1301">
                Questa anteprima è riservata. Inserisci la tua email per
                verificare l'accesso.
              </p>
              <div className="space-y-4" data-id="element-1302">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="La tua email" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d]" data-id="element-1303" />
                {accessError && <p className="text-sm text-red-600" data-id="element-1304">{accessError}</p>}
                <div className="flex justify-end space-x-2" data-id="element-1305">
                  <button onClick={() => setShowAccessForm(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors" data-id="element-1306">
                    Annulla
                  </button>
                  <button onClick={checkWhitelistAccess} className="px-4 py-2 bg-[#161c2d] text-white rounded-md text-sm hover:bg-[#1e2740] transition-colors" data-id="element-1307">
                    Verifica Accesso
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Reader Modal */}
        {showReaderModal && hasAccessToPreview && <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-id="element-1308">
            {/* Reader Header */}
            <div className="bg-white p-4 flex items-center justify-between shadow-md" data-id="element-1309">
              <button onClick={() => setShowReaderModal(false)} className="flex items-center text-gray-700 hover:text-gray-900" data-id="element-1310">
                <X size={20} className="mr-2" data-id="element-1311" />
                Chiudi
              </button>
              <div className="text-center flex-1" data-id="element-1312">
                <h3 className="text-lg font-semibold text-gray-900 truncate max-w-md mx-auto" data-id="element-1313">
                  {book.title}
                </h3>
                <p className="text-xs text-gray-500" data-id="element-1314">
                  Anteprima ({book.preview.percentage}%)
                </p>
              </div>
              <div className="w-[72px]" data-id="element-1315">{/* Placeholder for balance */}</div>
            </div>

            {/* Reader Content */}
            <div className="flex-1 overflow-auto bg-white max-w-3xl mx-auto w-full my-8 shadow-2xl rounded-lg" data-id="element-1316">
              <div className="p-8 sm:p-12 reader-content" data-id="element-1317">
                {previewContent ? <div className="prose prose-sm md:prose max-w-none" dangerouslySetInnerHTML={{
              __html: previewContent
            }} data-id="element-1318"></div> : <div className="text-center py-12" data-id="element-1319">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mx-auto mb-4" data-id="element-1320"></div>
                    <p className="text-gray-600" data-id="element-1321">Caricamento anteprima...</p>
                  </div>}
                {/* Fade out effect at the bottom */}
                <div className="h-32 bg-gradient-to-t from-white to-transparent -mt-32 relative" data-id="element-1322"></div>
                {/* CTA at the bottom */}
                <div className="text-center mt-6 p-6 bg-gray-50 rounded-lg border border-gray-100 relative" data-id="element-1323">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1324">
                    Ti è piaciuta l'anteprima?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1325">
                    Acquista il libro completo per continuare la lettura
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-1326">
                    <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm" data-id="element-1327">
                      Contatta l'Autore
                    </button>
                    <button className="px-4 py-2 bg-[#C6D6AB] text-gray-800 rounded-md hover:bg-[#b8c99a] transition-colors text-sm" data-id="element-1328">
                      Acquista il Libro
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Reader Navigation */}
            <div className="bg-white p-4 flex items-center justify-between shadow-md" data-id="element-1329">
              <button onClick={goToPreviousPage} className={`px-3 py-1.5 rounded-md text-sm flex items-center ${currentPage > 1 ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-50 text-gray-400 cursor-not-allowed'} transition-colors`} disabled={currentPage <= 1} data-id="element-1330">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-1331" />
                Pagina Precedente
              </button>
              <div className="text-sm text-gray-500" data-id="element-1332">
                Anteprima: pagina {currentPage} di {totalPages}
              </div>
              <button onClick={goToNextPage} className={`px-3 py-1.5 rounded-md text-sm flex items-center ${currentPage < totalPages ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-50 text-gray-400 cursor-not-allowed'} transition-colors`} disabled={currentPage >= totalPages} data-id="element-1333">
                Pagina Successiva
                <ArrowRight size={16} className="ml-1.5" data-id="element-1334" />
              </button>
            </div>
          </div>}

        {/* Share Modal */}
        {showShareModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1335">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1336">
              <div className="flex items-center justify-between px-4 py-3 border-b" data-id="element-1337">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-1338">
                  Condividi
                </h3>
                <button onClick={() => {
              setShowShareModal(false);
              setShowQRCode(false);
            }} className="text-gray-500 hover:text-gray-700" data-id="element-1339">
                  <X size={20} data-id="element-1340" />
                </button>
              </div>
              <div className="p-4" data-id="element-1341">
                {showQRCode ? <div className="text-center" data-id="element-1342">
                    <div className="flex justify-center mb-4" data-id="element-1343">
                      <QRCodeSVG value={window.location.href} size={200} includeMargin={true} bgColor={'#ffffff'} fgColor={'#161c2d'} data-id="element-1344" />
                    </div>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1345">
                      Scansiona questo codice QR per condividere il libro
                    </p>
                    <button onClick={() => setShowQRCode(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors" data-id="element-1346">
                      Torna alle opzioni
                    </button>
                  </div> : <>
                    <div className="mb-4" data-id="element-1347">
                      <p className="text-sm text-gray-600 mb-3" data-id="element-1348">
                        Condividi questo libro sui social media o copia il link
                      </p>
                      <div className="flex justify-center space-x-4 mb-6" data-id="element-1349">
                        <button className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:bg-[#1877F2]/90 transition-colors" data-id="element-1350">
                          <Facebook size={20} data-id="element-1351" />
                        </button>
                        <button className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:bg-[#1DA1F2]/90 transition-colors" data-id="element-1352">
                          <Twitter size={20} data-id="element-1353" />
                        </button>
                        <button className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:bg-[#0A66C2]/90 transition-colors" data-id="element-1354">
                          <Linkedin size={20} data-id="element-1355" />
                        </button>
                      </div>
                      <div className="flex items-center" data-id="element-1356">
                        <input type="text" value={window.location.href} readOnly className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-[#161c2d]/20 focus:border-[#161c2d]" data-id="element-1357" />
                        <button onClick={copyLink} className="px-3 py-2 bg-[#C6D6AB] text-gray-800 rounded-r-md text-sm hover:bg-[#b8c99a] transition-colors" data-id="element-1358">
                          <Copy size={16} data-id="element-1359" />
                        </button>
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1360">
                      <button onClick={() => setShowQRCode(true)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors" data-id="element-1361">
                        Mostra Codice QR
                      </button>
                    </div>
                  </>}
              </div>
            </div>
          </div>}

        <div className="mb-12" data-id="element-1362"></div>
      </div>

      {/* Footer con contatti */}
      <footer className="bg-[#376770] py-12 border-t border-[#2a3349]/50" data-id="element-1363">
        <div className="max-w-7xl mx-auto px-8 sm:px-10 lg:px-12" data-id="element-1364">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1365">
            <div data-id="element-1366">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-1367">
                Contatti
              </h3>
              <p className="text-gray-300 mb-4" data-id="element-1368">
                La piattaforma dedicata agli autori per proteggere e condividere
                le proprie opere.
              </p>
              <div className="text-gray-300" data-id="element-1369">
                <div className="mb-2" data-id="element-1370">
                  {currentUser.username || currentUser.name.toLowerCase().replace(' ', '.')}
                  @email.com
                </div>
                <div data-id="element-1371">+39 123 456 7890</div>
              </div>
            </div>
            <div className="md:text-right" data-id="element-1372">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-1373">Seguimi</h3>
              <div className="flex space-x-4 md:justify-end" data-id="element-1374">
                {currentUser.socialLinks?.instagram && <a href={currentUser.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-pink-400 transition-colors" data-id="element-1375">
                    <Instagram size={20} data-id="element-1376" />
                  </a>}
                {currentUser.socialLinks?.twitter && <a href={currentUser.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-blue-400 transition-colors" data-id="element-1377">
                    <Twitter size={20} data-id="element-1378" />
                  </a>}
                {currentUser.socialLinks?.facebook && <a href={currentUser.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-blue-500 transition-colors" data-id="element-1379">
                    <Facebook size={20} data-id="element-1380" />
                  </a>}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-[#2a3349]/50 text-center text-gray-300 text-sm" data-id="element-1381">
            <div className="flex justify-center mb-2" data-id="element-1382">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jJkprGARCzfGvbLTaJLb3b/Elze_logo.svg" alt="Elze Logo" className="h-8 w-auto filter brightness-0 invert-[1]" data-id="element-1383" />
            </div>
            <p data-id="element-1384">© 2023 Elze. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>;
}